import { useState, type ReactNode } from 'react';
import { GearSixIcon, ArrowSquareOutIcon } from '@phosphor-icons/react';
import { Num, T, Var, useGT } from 'gt-react';
import {
  type WorkbookColumnarSheet,
  type WorkbookDefinition,
  type WorkbookTable,
  type WorkbookResult,
  type WorkbookColumnType,
} from '@erp/shared-types';
import { formulaInputReferences, type WorkbookPlan } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { WorksheetInputGrid, WorksheetOutputGrid } from './WorksheetGrid';
import { WorksheetColumnControls } from './WorksheetColumnControls';
import { WorksheetAddColumn } from './WorksheetAddColumn';
import {
  addColumnDefaultTypes,
  lookupValueType,
  moveWorksheetColumn,
  removeWorksheetColumn,
  reorderWorksheetColumns,
} from './worksheetColumns';
import type { ChangeSheet } from './useWorkbookEditor';
import { WorksheetDiagnostics } from './WorksheetDiagnostics';
import { WorkbookItemIcon } from './workbookItemIcon';
import { workbookItemIconKind } from './workbookItemIconKind';
import { useWorkbookRunTime } from './workbookRunTime';
import { FirstLookLabel, LivePreviewLabel } from './LivePreviewLabel';
import type { FreshSource } from './useWorkbookPreview';
import { useWorksheetResultView } from './useWorksheetResultView';
import {
  WorksheetResultControls,
  WorksheetResultSample,
} from './WorksheetResultViews';
import { worksheetReadableTables } from './worksheetReadableTables';
import {
  formulaTables,
  worksheetFormulaFields,
} from './worksheetFormulaFields';

/** The Live preview's state for this Worksheet. */
export type SheetPreview = {
  /** The shown Result is the Live preview's. */
  shown: boolean;
  inputsAsOf: string | null;
  freshSources: readonly FreshSource[];
  reusedSourceCount: number;
  /** A first look, built on no Run: how many of each Source's first rows it read. */
  headRows: number | null;
  pending: boolean;
  error: string | null;
  /** The shown Result is a Run on sample's: how many key values it took of how many. */
  sample?: { keys: { sample: number; full: number } | null };
};

// Inherited complexity: this branch changed PreviewStatus and SheetPreview beside it, not this component's branches.
// fallow-ignore-next-line complexity
export function SheetEditor({
  workbookId,
  runId,
  sheet,
  definition,
  plan,
  input,
  result,
  stale,
  ranAt,
  preview,
  needsRunHint,
  runMenu,
  readOnly,
  change,
  sourceTypes,
  onInputChange,
  openItem,
}: {
  workbookId: string;
  /** The Run of the shown Result; absent for a Live preview or without one. */
  runId?: string;
  sheet: WorkbookColumnarSheet;
  definition: WorkbookDefinition;
  plan: WorkbookPlan;
  input?: WorkbookTable;
  result?: WorkbookResult;
  stale: boolean;
  /** When the shown Result's Run was created. */
  ranAt?: string;
  preview?: SheetPreview;
  /** What fills cells without a Current or Live preview Result. */
  needsRunHint: string;
  runMenu?: ReactNode;
  readOnly: boolean;
  change: ChangeSheet;
  /** The types the input Source gives its columns, by name. */
  sourceTypes?: ReadonlyMap<string, WorkbookColumnType>;
  onInputChange: (inputId: string) => void;
  openItem?: (id: string) => void;
}) {
  const gt = useGT();
  const runTime = useWorkbookRunTime(definition);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [sourcePreviewOpen, setSourcePreviewOpen] = useState(false);
  const [selectedInputs, setSelectedInputs] = useState<string[]>([]);
  const resultView = useWorksheetResultView({
    workbookId,
    runId,
    sheet,
    result,
    readOnly,
    change,
  });
  const index = Math.min(selectedIndex, sheet.columns.length - 1);
  const items = [...definition.sources, ...definition.sheets].filter(
    (item) => item.id !== sheet.id,
  );
  // Lookup sources and inputs never offer a Worksheet that reads this one.
  const readable = worksheetReadableTables(definition, sheet.id);
  // The current input stays listed even while it is part of a cycle.
  const current = items.filter(
    (item) =>
      item.id === sheet.inputId &&
      !readable.some((entry) => entry.id === item.id),
  );
  const inputs = [...current, ...readable];
  const inputName = items.find((item) => item.id === sheet.inputId)?.name ?? '';
  const inputIcon = workbookItemIconKind(definition, sheet.inputId) ?? 'data';
  const inputColumns = input?.columns ?? [];
  const defaultTypes = addColumnDefaultTypes(sheet, definition, plan, input);
  const diagnostics = plan.diagnostics.filter(
    (diagnostic) => diagnostic.location.itemId === sheet.id,
  );
  const selectedDiagnostics = diagnostics.filter(
    ({ location }) => location.part === 'column' && location.index === index,
  );
  const references = formulaInputReferences(
    sheet.columns[index]?.expression ?? '',
    inputColumns,
  );
  const formulaFields = (columnIndex?: number) =>
    worksheetFormulaFields({
      sheet,
      sheetPlan: plan.sheets.get(sheet.id),
      inputName,
      inputIcon,
      input,
      itemName: (id) => items.find((item) => item.id === id)?.name ?? '',
      itemIcon: (id) => workbookItemIconKind(definition, id),
      columnIndex,
      tables: formulaTables(definition, plan, sheet.id),
    });
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <div className="flex items-center gap-2">
        <h2 className="mr-auto truncate text-sm font-medium">{sheet.name}</h2>
        <PreviewStatus preview={preview} definition={definition} />
        {runMenu}
        <Popover open={sourcePreviewOpen} onOpenChange={setSourcePreviewOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm">
              <WorkbookItemIcon kind={inputIcon} />
              <T context="worksheet source preview action">Source data</T>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-[48rem] max-w-[calc(100vw-2rem)] flex flex-col gap-3"
            aria-label={gt('Source data', {
              context: 'worksheet source preview action',
            })}
          >
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-medium">{inputName}</h3>
              {openItem && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSourcePreviewOpen(false);
                    openItem(sheet.inputId);
                  }}
                >
                  <ArrowSquareOutIcon />
                  <T context="worksheet open source tab">Open source tab</T>
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              <T context="worksheet source preview guidance">
                Source preview. Select columns here or use @ in a formula to
                reference them.
              </T>
            </p>
            {input ? (
              <WorksheetInputGrid
                table={input}
                references={references}
                sheet={sheet}
                result={result}
                change={change}
                selected={selectedInputs}
                readOnly={readOnly}
                toggle={(name) =>
                  setSelectedInputs((selected) =>
                    selected.includes(name)
                      ? selected.filter((item) => item !== name)
                      : [...selected, name],
                  )
                }
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                <T context="worksheet source preview unavailable">
                  Source preview is not available yet.
                </T>
              </p>
            )}
          </PopoverContent>
        </Popover>
        <WorksheetAddColumn
          sheet={sheet}
          inputColumns={inputColumns}
          formulaFields={formulaFields()}
          defaultTypes={defaultTypes}
          lookupType={(lookup) => lookupValueType(plan, lookup)}
          items={readable}
          selected={selectedInputs}
          setSelected={setSelectedInputs}
          readOnly={readOnly}
          change={change}
          onAdded={setSelectedIndex}
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label={gt('Worksheet settings', {
                context: 'worksheet settings action',
              })}
            >
              <GearSixIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            aria-label={gt('Worksheet settings', {
              context: 'worksheet settings action',
            })}
          >
            <fieldset disabled={readOnly} className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-xs">
                <T context="worksheet name">Worksheet name</T>
                <Input
                  value={sheet.name}
                  onChange={(event) =>
                    change((latest) => ({
                      ...latest,
                      name: event.target.value,
                    }))
                  }
                />
              </label>
              <Select
                disabled={readOnly}
                value={sheet.inputId}
                onValueChange={(id) => {
                  setSelectedInputs([]);
                  onInputChange(id);
                }}
              >
                <SelectTrigger
                  aria-label={gt('Data', { context: 'worksheet input' })}
                  className="w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {inputs.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </fieldset>
          </PopoverContent>
        </Popover>
      </div>
      <WorksheetDiagnostics
        sheet={sheet}
        definition={definition}
        diagnostics={diagnostics}
        selectColumn={setSelectedIndex}
      />
      <section
        className="flex flex-1 flex-col"
        aria-label={gt('Output columns', {
          context: 'worksheet output section',
        })}
      >
        <WorksheetResultControls
          sheet={sheet}
          result={result}
          view={resultView}
        />
        <WorksheetOutputGrid
          focus={resultView.focus}
          sheet={sheet}
          sheetPlan={plan.sheets.get(sheet.id)}
          result={result}
          stale={stale}
          needsRunHint={needsRunHint}
          invalidColumns={
            new Set(
              diagnostics.flatMap(({ location }) =>
                location.part === 'column' ? [location.index] : [],
              ),
            )
          }
          selected={index}
          select={setSelectedIndex}
          change={change}
          readOnly={readOnly}
          summaryAside={
            <WorksheetResultSample
              sheet={sheet}
              result={result}
              view={resultView}
            />
          }
          toolbar={
            <fieldset disabled={readOnly}>
              <WorksheetColumnControls
                sheet={sheet}
                key={index}
                formulaFields={formulaFields(index)}
                readOnly={readOnly}
                index={index}
                diagnostics={selectedDiagnostics}
                sourceTypes={sourceTypes}
                change={change}
                move={(delta) => {
                  change((latest) => moveWorksheetColumn(latest, index, delta));
                  setSelectedIndex(index + delta);
                }}
                rearrange={(order) => {
                  change((latest) => reorderWorksheetColumns(latest, order));
                  // The selection follows its column to its new place.
                  setSelectedIndex(Math.max(0, order.indexOf(index)));
                }}
                remove={() => {
                  change((latest) => removeWorksheetColumn(latest, index));
                  setSelectedIndex(Math.max(0, index - 1));
                }}
              />
            </fieldset>
          }
        />
        {stale && result && (
          <p
            role="status"
            className="mt-2 text-xs text-muted-foreground"
            title={ranAt ? runTime.full(ranAt) : undefined}
          >
            {ranAt ? (
              <T context="workbook stale result with its run time">
                This worksheet or its data changed since the run at{' '}
                <Var>{runTime.short(ranAt)}</Var>. Run again to reflect your
                changes. Sort changes reorder these preview rows right away.
              </T>
            ) : (
              <T context="workbook stale result">
                This worksheet or its data changed since this run. Run again to
                reflect your changes. Sort changes reorder these preview rows
                right away.
              </T>
            )}
          </p>
        )}
      </section>
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          <T context="worksheet formula reference hint">
            Type @ in a formula to reference a column.
          </T>
        </span>
        {/* With a Result, the output grid's toolbar shows its row counts. */}
        {!result && <span>{needsRunHint}</span>}
      </div>
    </div>
  );
}

/** "Preview · inputs as of <time>" while the grid shows a Live preview; its progress and failure otherwise. */
export function PreviewStatus({
  preview,
  definition,
}: {
  preview?: SheetPreview;
  definition: WorkbookDefinition;
}) {
  const runTime = useWorkbookRunTime(definition);
  if (!preview) return null;
  if (preview.sample)
    return (
      <span
        role="status"
        className="shrink-0 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200"
      >
        {preview.sample.keys ? (
          <T context="worksheet sample result label">
            Sample result · <Num>{preview.sample.keys.sample}</Num> of{' '}
            <Num>{preview.sample.keys.full}</Num> key values
          </T>
        ) : (
          <T context="worksheet sample result label">Sample result</T>
        )}
      </span>
    );
  if (preview.shown && preview.headRows !== null)
    return <FirstLookLabel preview={preview} headRows={preview.headRows} />;
  if (preview.shown && preview.inputsAsOf)
    return (
      <LivePreviewLabel
        preview={preview}
        inputsAsOf={preview.inputsAsOf}
        runTime={runTime}
      />
    );
  if (preview.pending)
    return (
      <span role="status" className="shrink-0 text-xs text-muted-foreground">
        <T context="worksheet live preview computing">Updating preview…</T>
      </span>
    );
  if (preview.error)
    return (
      <span
        role="status"
        className="max-w-80 shrink-0 truncate text-xs text-muted-foreground"
        title={preview.error}
      >
        <T context="worksheet live preview failed">
          Preview unavailable: <Var>{preview.error}</Var>
        </T>
      </span>
    );
  return null;
}
